import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../context/ToastContext";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ProductApi from "../../services/ProductApi";
import ListOperationApi from "../../services/OperationApi";
import FailureApi from "../../services/FailureApi";

// Design System
import FormCard from "../../components/FormCard";
import Input from "../../components/Input/Input";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";

interface Option {
  id: string;
  name: string;
}

const EditFormFailure: React.FC = () => {
  const { fmeaId } = useParams<{ fmeaId: string }>();
  console.log("fmeaId", fmeaId);
  const { t: translate } = useTranslation("form");
  const { displayToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fmeaSource = searchParams.get("type") || "product";
  const { id } = useParams<{ id: string }>();

  // refs for navigation
  const [operations, setOperations] = useState<Option[]>([]);
  const [causes, setCauses] = useState<Option[]>([]);
  const [operation, setOperation] = useState<Option | null>(null);

  const [descricao, setDescricao] = useState("");
  const [effect, setEffect] = useState("");
  const [code, setCode] = useState("");
  const [severity, setSeverity] = useState("");
  const [occurrence, setOccurrence] = useState("");
  const [detectability, setDetectability] = useState("");
  const [rpn, setRpn] = useState("");
  const [version, setVersion] = useState("");
  const [cause, setCause] = useState<Option | null>(null);

  const [errors, setErrors] = useState<Record<string, string>>({
    nome: "",
    descricao: "",
    effect: "",
    severity: "",
    occurrence: "",
    detectability: "",
  });
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  // calcula RPN com base em severidade, ocorrência e detectabilidade
  useEffect(() => {
    const s = parseInt(severity || "0", 10);
    const o = parseInt(occurrence || "0", 10);
    const d = parseInt(detectability || "0", 10);
    if (!isNaN(s) && !isNaN(o) && !isNaN(d)) {
      setRpn((s * o * d).toString());
    }
  }, [severity, occurrence, detectability]);
  // Carregamento encadeado dos dropdowns dependentes ao buscar o FailureResponse
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await FailureApi.GetFailureResponseById(id);
        const f = res.data;
        if (!f) throw new Error("Failure not found");

        // Busca as opções filtradas por ID usando EditFormApi
        const [operationsRes, causasRes] = await Promise.all([
          ListOperationApi.GetOperationsBySubLineId(f.sublineId),
          FailureApi.GetAllCauses(),
          ProductApi.GetAll(),
        ]);
        setOperations(operationsRes.data || []);
        setCauses(causasRes.data || []);
        setOperation(
          (operationsRes.data || []).find((o: any) => o.id === f.operationId) ||
            null,
        );
        setDescricao(f.description);
        setEffect(f.effect);
        setCode(String(f.code));
        setSeverity(String(f.severity));
        setOccurrence(String(f.occurrence));
        setDetectability(String(f.detectability));
        setRpn(String(f.rpn));
        setVersion(String(f.version));
        setCause(
          (causasRes.data || []).find(
            (c: any) => String(c.id) === String(f.causeId),
          ) || null,
        );
      } catch (error) {
        displayToast("error", translate("ErrorLoading"), "", false, 3000);
      }
    })();
  }, [id]);

  const validate = (): boolean => {
    const newErr: Record<string, string> = {
      descricao: "",
      effect: "",
      severity: "",
      occurrence: "",
      detectability: "",
    };

    // Descrição validation
    if (!descricao.trim()) newErr.descricao = translate("DescriptionRequired");
    else if (descricao.length > 500)
      newErr.descricao = translate("DescriptionExceptionCharacters");

    // Effect validation
    if (!effect.trim())
      newErr.effect = translate("EffectRequired"); // comment: validation for empty effect
    else if (effect.length > 50)
      newErr.effect = translate("EffectExceptionCharacters");

    // Severity validation
    if (!severity.trim()) newErr.severity = translate("SeverityRequired"); // comment: validation for empty severity

    // Occurrence validation
    if (!occurrence.trim()) newErr.occurrence = translate("OccurrenceRequired"); // comment: validation for empty occurrence

    // Detectability validation
    if (!detectability.trim())
      newErr.detectability = translate("DetectabilityRequired"); // comment: validation for empty detectability

    setErrors(newErr);
    // returns true if no errors
    return Object.values(newErr).every((e) => !e);
  };

  const handleSave = async (): Promise<void> => {
    if (!validate()) return;
    try {
      const payload = {
        id: id!,
        description: descricao,
        effect: effect,
        causeId: cause?.id,
        severity: Number(severity),
        occurrence: Number(occurrence),
        detectability: Number(detectability),
      };
      await FailureApi.Update(payload);
      displayToast(
        "success",
        translate("Success"),
        translate("FailureUpdated"),
        false,
        3000,
      );
      setTimeout(() => navigate(`/fmeas/${fmeaId}/failures?type=${fmeaSource}`), 1000);
    } catch (error: any) {
      const msg =
        (error?.response?.data?.message as string) ??
        error?.message ??
        translate("ErrorUnexpected");
      displayToast("error", translate("Error"), msg, false, 3000);
    }
  };

  return (
    <FormCard
      title={translate("EditFailure")}
      icon="edit"
      onSubmit={handleSave}
      onCancel={() => setShowConfirmCancel(true)}
      submitButtonLabel={translate("SaveFailure")}
      cancelButtonLabel={translate("Cancel")}
      numColumns={6}
    >
      <div style={{ gridColumn: "span 6" }}>
        <Input
          type="select"
          label={translate("selectOperation")}
          required
          value={operation}
          options={operations}
          optionLabel="name"
          placeholder={translate("selectOperation")}
          disabled
          onChange={() => {}}
        />
      </div>

      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="text"
          label={translate("code")}
          required
          value={code}
          disabled
          placeholder={translate("code")}
          onChange={() => {}}
        />
      </div>

      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="text"
          label={translate("version")}
          required
          value={version}
          disabled
          placeholder={translate("version")}
          onChange={() => {}}
        />
      </div>

      <div style={{ gridColumn: "span 6" }}>
        <Input
          type="textArea"
          label={translate("Description")}
          required
          value={descricao}
          onChange={(val: any) => setDescricao(val)}
          placeholder={translate("Description")}
          rows={3}
          maxLength={600}
          className={errors.descricao ? "p-invalid" : ""}
        />
        {errors.descricao && <small className="p-error">{errors.descricao}</small>}
      </div>

      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="text"
          label={translate("effect")}
          required
          value={effect}
          onChange={(val: any) => setEffect(val)}
          placeholder={translate("effect")}
          maxLength={300}
          className={errors.effect ? "p-invalid" : ""}
        />
        {errors.effect && <small className="p-error">{errors.effect}</small>}
      </div>

      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="select"
          label={translate("cause")}
          required
          value={cause}
          options={causes}
          optionLabel="name"
          placeholder={translate("cause")}
          onChange={(val: any) => setCause(val)}
        />
      </div>

      <div style={{ gridColumn: "span 2" }}>
        <Input
          type="number"
          label={translate("severity")}
          required
          value={severity}
          onChange={(val: any) => setSeverity(String(val))}
          placeholder={translate("severity")}
        />
      </div>

      <div style={{ gridColumn: "span 2" }}>
        <Input
          type="number"
          label={translate("occurrence")}
          required
          value={occurrence}
          onChange={(val: any) => setOccurrence(String(val))}
          placeholder={translate("occurrence")}
        />
      </div>

      <div style={{ gridColumn: "span 2" }}>
        <Input
          type="number"
          label={translate("detectability")}
          required
          value={detectability}
          onChange={(val: any) => setDetectability(String(val))}
          placeholder={translate("detectability")}
        />
      </div>

      <div style={{ gridColumn: "span 6" }}>
        <Input
          type="text"
          label="RPN"
          value={rpn}
          disabled
          placeholder="RPN"
          onChange={() => {}}
        />
      </div>

      <ConfirmModal
        visible={showConfirmCancel}
        onHide={() => setShowConfirmCancel(false)}
        onConfirm={() => navigate(`/fmeas/${fmeaId}/failures?type=${fmeaSource}`)}
      />
    </FormCard>
  );
};

export default EditFormFailure;
